import React, { useState, useRef, useEffect } from 'react';
import SuggestionItem from './SuggestionItem';
import { ScheduleSuggestion } from '../types/AutoScheduling';

interface SuggestionListProps {
    suggestions: ScheduleSuggestion[];
    onSelect: (suggestion: ScheduleSuggestion) => void;
    sortBy: 'score' | 'time' | 'room';
    onSortChange: (sortBy: 'score' | 'time' | 'room') => void;
    isLoading?: boolean;
    loadingProgress?: number;
    loadingMessage?: string;
    disabled?: boolean;
}

/**
 * Display the top 10 schedule suggestions with conflict details and sorting functionality
 */
const SuggestionList: React.FC<SuggestionListProps> = ({
    suggestions,
    onSelect,
    sortBy,
    onSortChange,
    isLoading = false,
    loadingProgress = 0,
    loadingMessage = 'Loading...',
    disabled = false
}) => {
    const [focusedIndex, setFocusedIndex] = useState<number>(-1);
    const suggestionRefs = useRef<(HTMLDivElement | null)[]>([]);
    const sortDropdownRef = useRef<HTMLSelectElement>(null);

    // Sort suggestions based on current sort criteria
    const sortedSuggestions = React.useMemo(() => {
        const sorted = [...suggestions].sort((a, b) => {
            switch (sortBy) {
                case 'score':
                    // Primary: conflict score (ascending)
                    if (a.conflictScore !== b.conflictScore) {
                        return a.conflictScore - b.conflictScore;
                    }
                    // Secondary: time (earliest first)
                    if (a.timeStart !== b.timeStart) {
                        return a.timeStart.localeCompare(b.timeStart);
                    }
                    // Tertiary: room (alphabetical)
                    return a.room.localeCompare(b.room);

                case 'time':
                    // Primary: day of week (Monday first)
                    const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
                    const dayDiff = dayOrder.indexOf(a.day.toLowerCase()) - dayOrder.indexOf(b.day.toLowerCase());
                    if (dayDiff !== 0) {
                        return dayDiff;
                    }
                    // Secondary: time (earliest first)
                    if (a.timeStart !== b.timeStart) {
                        return a.timeStart.localeCompare(b.timeStart);
                    }
                    // Tertiary: conflict score (ascending)
                    return a.conflictScore - b.conflictScore;

                case 'room':
                    // Primary: room (alphabetical)
                    if (a.room !== b.room) {
                        return a.room.localeCompare(b.room);
                    }
                    // Secondary: conflict score (ascending)
                    if (a.conflictScore !== b.conflictScore) {
                        return a.conflictScore - b.conflictScore;
                    }
                    // Tertiary: time (earliest first)
                    return a.timeStart.localeCompare(b.timeStart);

                default:
                    return 0;
            }
        });

        // Return top 10 suggestions
        return sorted.slice(0, 10);
    }, [suggestions, sortBy]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!sortedSuggestions.length) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setFocusedIndex(prev => {
                        const next = prev < sortedSuggestions.length - 1 ? prev + 1 : 0;
                        suggestionRefs.current[next]?.focus();
                        return next;
                    });
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    setFocusedIndex(prev => {
                        const next = prev > 0 ? prev - 1 : sortedSuggestions.length - 1;
                        suggestionRefs.current[next]?.focus();
                        return next;
                    });
                    break;

                case 'Enter':
                case ' ':
                    if (focusedIndex >= 0 && focusedIndex < sortedSuggestions.length) {
                        event.preventDefault();
                        const selectedSuggestion = sortedSuggestions[focusedIndex];
                        if (selectedSuggestion) {
                            onSelect(selectedSuggestion);
                        }
                    }
                    break;

                case 'Escape':
                    setFocusedIndex(-1);
                    sortDropdownRef.current?.focus();
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [sortedSuggestions, focusedIndex, onSelect]);

    // Reset focused index when suggestions change
    useEffect(() => {
        setFocusedIndex(-1);
        suggestionRefs.current = suggestionRefs.current.slice(0, sortedSuggestions.length);
    }, [sortedSuggestions]);

    const getSortLabel = (sortOption: 'score' | 'time' | 'room') => {
        switch (sortOption) {
            case 'score':
                return 'Conflict Score';
            case 'time':
                return 'Time';
            case 'room':
                return 'Room';
            default:
                return sortOption;
        }
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-gray-900">
                        Generating Suggestions...
                    </h3>
                    <div className="text-sm text-gray-500">
                        {loadingProgress > 0 && `${Math.round(loadingProgress)}%`}
                    </div>
                </div>

                {/* Loading message */}
                {loadingMessage && (
                    <div className="text-sm text-gray-600 text-center">
                        {loadingMessage}
                    </div>
                )}

                {/* Progress bar */}
                <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${loadingProgress}%` }}
                        role="progressbar"
                        aria-valuenow={loadingProgress}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        aria-label={`Loading progress: ${Math.round(loadingProgress)}%`}
                    />
                </div>

                {/* Loading skeleton */}
                <div className="space-y-3">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="border rounded-lg p-4 animate-pulse">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-6 bg-gray-200 rounded"></div>
                                    <div className="w-24 h-6 bg-gray-200 rounded-full"></div>
                                </div>
                                <div className="w-20 h-8 bg-gray-200 rounded"></div>
                            </div>
                            <div className="flex items-center gap-4 mb-3">
                                <div className="w-12 h-6 bg-gray-200 rounded"></div>
                                <div className="w-32 h-6 bg-gray-200 rounded"></div>
                                <div className="w-20 h-6 bg-gray-200 rounded"></div>
                            </div>
                            <div className="w-48 h-4 bg-gray-200 rounded"></div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    // Empty state
    if (!sortedSuggestions.length) {
        return (
            <div className="text-center py-12">
                <div className="text-6xl mb-4" role="img" aria-label="No suggestions">
                    📅
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    No Suggestions Found
                </h3>
                <p className="text-gray-600 mb-4 max-w-md mx-auto">
                    No available time slots match your criteria. Try adjusting your parameters:
                </p>
                <ul className="text-sm text-gray-500 space-y-1 max-w-md mx-auto">
                    <li>• Expand your time range</li>
                    <li>• Select more days</li>
                    <li>• Include more rooms</li>
                    <li>• Choose different weeks</li>
                </ul>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Header with sorting */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                    Suggestions ({sortedSuggestions.length} of {suggestions.length})
                </h3>
                <div className="flex items-center gap-2">
                    <label htmlFor="sort-select" className="text-sm text-gray-600">
                        Sort by:
                    </label>
                    <select
                        id="sort-select"
                        ref={sortDropdownRef}
                        value={sortBy}
                        onChange={(e) => onSortChange(e.target.value as 'score' | 'time' | 'room')}
                        className="text-sm border border-gray-300 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        aria-label="Sort suggestions by"
                    >
                        <option value="score">Conflict Score</option>
                        <option value="time">Time</option>
                        <option value="room">Room</option>
                    </select>
                </div>
            </div>

            {/* Suggestions list */}
            <div
                className="space-y-3"
                role="listbox"
                aria-label={`Schedule suggestions sorted by ${getSortLabel(sortBy)}`}
            >
                {sortedSuggestions.map((suggestion, index) => (
                    <div
                        key={suggestion.id}
                        ref={(el) => {
                            suggestionRefs.current[index] = el;
                        }}
                        tabIndex={focusedIndex === index ? 0 : -1}
                        className={`focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded-lg ${focusedIndex === index ? 'ring-2 ring-blue-500 ring-offset-2' : ''
                            }`}
                        role="option"
                        aria-selected={focusedIndex === index}
                        onFocus={() => setFocusedIndex(index)}
                        onBlur={() => {
                            // Only clear focus if not moving to another suggestion
                            setTimeout(() => {
                                if (!suggestionRefs.current.some(ref => ref === document.activeElement)) {
                                    setFocusedIndex(-1);
                                }
                            }, 0);
                        }}
                    >
                        <SuggestionItem
                            suggestion={suggestion}
                            onSelect={() => onSelect(suggestion)}
                            rank={index + 1}
                            disabled={disabled}
                        />
                    </div>
                ))}
            </div>

            {/* Show more indicator if there are more than 10 suggestions */}
            {suggestions.length > 10 && (
                <div className="text-center py-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                        Showing top 10 of {suggestions.length} suggestions
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                        Adjust your parameters to see different options
                    </p>
                </div>
            )}
        </div>
    );
};

export default SuggestionList;